import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Mail, MapPin, Clock } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_41f280ff-5c99-48b4-9414-474d5e72c2fc/artifacts/y9kopzrq_Black%20Elegant%20Modern%20Name%20Initials%20Monogram%20Logo.png";

export const Footer = () => {
  return (
    <footer className="bg-slate-900 text-white" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Logo & About */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={LOGO_URL} 
                alt="K&K Medical and Dental" 
                className="h-14 w-auto bg-white rounded-lg p-1"
              />
              <div>
                <h3 className="text-lg font-bold text-white notranslate">K&K Medical & Dental</h3>
                <p className="text-teal-400 text-sm">Your Trusted Healthcare Partner</p>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Your trusted healthcare partner in Umzinto and Scottburgh. Providing quality medical and dental services at affordable rates.
            </p>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h3 className="text-lg font-semibold mb-6">Quick Links</h3>
            <div className="flex flex-wrap gap-x-6 gap-y-3">
              {[
                { name: 'Home', path: '/' },
                { name: 'About', path: '/about' },
                { name: 'Services', path: '/services' },
                { name: 'Price List', path: '/prices' },
                { name: 'Packages', path: '/packages' },
                { name: 'Weight Loss & Wellness', path: '/weight-loss' },
                { name: 'Promotions', path: '/promotions' },
                { name: 'FAQs', path: '/faqs' },
                { name: 'Contact Us', path: '/contact' },
                { name: 'Blog', path: '/blog' },
              ].map((link) => (
                <Link 
                  key={link.path}
                  to={link.path}
                  onClick={() => window.scrollTo(0, 0)}
                  className="text-slate-400 hover:text-teal-400 transition-colors duration-200"
                  data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-6">Contact Us</h3>
            <ul className="space-y-4">
              <li>
                <a 
                  href="tel:0399741570" 
                  className="flex items-center gap-3 text-slate-400 hover:text-teal-400 transition-colors duration-200"
                  data-testid="footer-phone"
                >
                  <Phone className="w-5 h-5 text-teal-500" />
                  <span>039 974 1570 (Umzinto)</span>
                </a>
              </li>
              <li>
                <a 
                  href="tel:0393155041" 
                  className="flex items-center gap-3 text-slate-400 hover:text-blue-400 transition-colors duration-200"
                  data-testid="footer-phone-scottburgh"
                >
                  <Phone className="w-5 h-5 text-blue-400" />
                  <span>039 315 5041 (Scottburgh)</span>
                </a>
              </li>
              <li>
                <a 
                  href="https://wa.me/27828951916" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-3 text-slate-400 hover:text-teal-400 transition-colors duration-200"
                  data-testid="footer-whatsapp"
                >
                  <MessageCircle className="w-5 h-5 text-teal-500" />
                  <span>082 895 1916 (WhatsApp)</span>
                </a>
              </li>
              <li>
                <a 
                  href="mailto:info@kkmedical.co.za" 
                  className="flex items-center gap-3 text-slate-400 hover:text-teal-400 transition-colors duration-200"
                  data-testid="footer-email"
                >
                  <Mail className="w-5 h-5 text-teal-500" />
                  <span>info@kkmedical.co.za</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                <div className="notranslate">
                  <p className="text-white text-sm font-medium mb-1">Umzinto</p>
                  <p className="text-sm">Lot 293 Nelson Mandela Road, Umzinto, KZN</p>
                </div>
              </li>
              <li className="flex items-start gap-3 text-slate-400">
                <MapPin className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="notranslate">
                  <p className="text-white text-sm font-medium mb-1">Scottburgh</p>
                  <p className="text-sm">Scott Health Medical Center, 41 Airth Street, Scottburgh, KZN</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Operating Hours */}
          <div className="notranslate">
            <h3 className="text-lg font-semibold mb-6">Operating Hours</h3>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-slate-400">
                <Clock className="w-5 h-5 text-teal-500" />
                <div>
                  <p className="font-medium text-white">Mon - Fri</p>
                  <p>8:00 AM - 5:00 PM</p>
                </div>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Clock className="w-5 h-5 text-teal-500" />
                <div>
                  <p className="font-medium text-white">Saturday</p>
                  <p>8:00 AM - 2:00 PM</p>
                </div>
              </li>
              <li className="flex items-center gap-3 text-slate-400">
                <Clock className="w-5 h-5 text-teal-500" />
                <div>
                  <p className="font-medium text-white">Sun & Holidays</p>
                  <p>Closed</p>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} <span className="notranslate">K&K Medical and Dental</span>. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <Link 
                to="/privacy"
                onClick={() => window.scrollTo(0, 0)}
                className="text-slate-400 hover:text-teal-400 transition-colors duration-200 text-sm"
                data-testid="footer-privacy-link"
              >
                Privacy Notice
              </Link>
              <Link
                to="/cancellation-refund-policy"
                onClick={() => window.scrollTo(0, 0)}
                className="text-slate-400 hover:text-teal-400 transition-colors duration-200 text-sm"
                data-testid="footer-cancellation-link"
              >
                Cancellation &amp; Refund Policy
              </Link>
              <a 
                href="https://g.page/r/CdrlVVVD9ZJgEBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-teal-400 transition-colors duration-200 text-sm"
                data-testid="footer-review-link"
              >
                Leave a Review
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
